import { useLayoutEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useShallow } from "zustand/shallow";
import type { Tool } from "@/app/tools/types";
import { extractToolName } from "@/app/tools/utils";
import { extractParameterValues } from "../utils";
import { usePlaygroundStore } from "./use-store";

type UseToolInitializationOptions = {
  tools: Tool[] | undefined;
  toolId: string | undefined;
  urlInput: Record<string, unknown> | undefined;
  form: UseFormReturn<Record<string, unknown>>;
};

/**
 * Finds a tool by its short name from the tools list.
 * Comparison is case-insensitive to handle variations in tool naming.
 */
function findToolByShortName(
  tools: Tool[],
  shortName: string
): Tool | undefined {
  const normalizedName = shortName.toLowerCase();
  return tools.find(
    (tool) => extractToolName(tool).toLowerCase() === normalizedName
  );
}

/**
 * Hook that initializes the selected tool on mount or when toolId changes.
 * Handles URL-based tool selection and form value restoration.
 */
export function useToolInitialization({
  tools,
  toolId,
  urlInput,
  form,
}: UseToolInitializationOptions) {
  const selectedTool = usePlaygroundStore((state) => state.selectedTool);
  const setSelectedTool = usePlaygroundStore((state) => state.setSelectedTool);
  // Use shallow comparison to prevent unnecessary effect re-runs when unrelated state changes
  const toolInputByFqn = usePlaygroundStore(
    useShallow((state) => state.toolInputByFqn)
  );

  const selectedToolName = selectedTool
    ? extractToolName(selectedTool)
    : undefined;

  // Extract specific form method to avoid including unstable form object in deps
  const { reset } = form;

  // Track if we've initialized on this mount to ensure form is reset on remount
  const hasInitializedRef = useRef(false);
  // Track the tools array identity to detect refetches
  const toolsIdentityRef = useRef(tools);

  // Reset initialization flag when tools data is refetched (e.g., after authorization)
  if (tools !== toolsIdentityRef.current) {
    toolsIdentityRef.current = tools;
    hasInitializedRef.current = false;
  }

  useLayoutEffect(() => {
    if (!tools?.length) {
      return;
    }

    // Check both that a tool is selected AND that it matches the requested toolId
    // Without the selectedTool check, undefined === undefined would incorrectly return true
    const isAlreadySelected = Boolean(
      selectedTool && selectedToolName?.toLowerCase() === toolId?.toLowerCase()
    );

    // Skip if tool is already selected AND we've already initialized on this mount
    if (isAlreadySelected && hasInitializedRef.current) {
      return;
    }

    const requestedTool = toolId
      ? findToolByShortName(tools, toolId)
      : undefined;
    const initialTool = requestedTool ?? tools[0];

    // Only update store if tool changed
    if (!isAlreadySelected) {
      setSelectedTool(initialTool);
    }

    const savedValues = toolInputByFqn[initialTool.fully_qualified_name];

    // Only use URL input when the toolId in the URL matches the tool being initialized.
    // When switching tools, the form resets to saved values (or empty if none saved).
    const hasUrlInput = urlInput && Object.keys(urlInput).length > 0;
    const inputValues = requestedTool && hasUrlInput ? urlInput : savedValues;
    reset(extractParameterValues(initialTool, inputValues));

    hasInitializedRef.current = true;
  }, [
    tools,
    toolId,
    selectedTool,
    selectedToolName,
    setSelectedTool,
    urlInput,
    reset,
    toolInputByFqn,
  ]);
}
